<script lang="ts">
	import { LiveIndicator } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import DashCameraCardInfoLine from './DashCameraCardInfoLine.svelte';
	import type { Device } from 'src/types/Device';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
	import type { VideoContainerState } from 'src/components/Camera/types/video-container';

	export let device: Device | undefined;
	export let status: DeviceStatusG2 | undefined;
	export let settings: DeviceSettingsG2 | undefined;
	/**
	 * The stream is really playing. Callers derive it from the player, so no
	 * online / blocked-state check is repeated here.
	 */
	export let showLiveBadge = false;
	export let compact = false;
	export let denseOverlay = false;
	export let iconOnly = false;
	/** Measured tile size — status label scale follows this, not denseOverlay. */
	export let overlaySize: 's' | 'm' | 'l' = 's';
	export let overlayState: VideoContainerState | undefined;
</script>

<div
	class="dash-camera-widget-header"
	class:compact
	class:dense-overlay={denseOverlay}
	class:size-s={overlaySize === 's'}
>
	{#if showLiveBadge}
		<span class="live-dot" role="status" aria-label={$format('label_live', { default: 'Live' })}>
			<LiveIndicator />
		</span>
	{/if}
	<div class="header-status-column">
		{#if device}
			<DashCameraCardInfoLine
				{device}
				{status}
				{settings}
				{compact}
				{denseOverlay}
				{iconOnly}
				{overlaySize}
				{overlayState}
				alignEnd
				onDark
			/>
		{/if}
	</div>
</div>

<style>
	.dash-camera-widget-header {
		display: flex;
		align-items: flex-start;
		justify-content: flex-end;
		gap: var(--spacing-xs);
		width: 100%;
		max-width: 100%;
		pointer-events: none;
		box-sizing: border-box;
	}

	/* Pinned left while the status column keeps to the right edge. The min-height
	   matches a status chip so the dot lines up with the labels across the row. */
	.live-dot {
		display: flex;
		align-items: center;
		min-height: 24px;
		margin-right: auto;
		flex-shrink: 0;
		line-height: 0;
	}

	.dash-camera-widget-header.dense-overlay.size-s .live-dot,
	.dash-camera-widget-header.compact.size-s .live-dot {
		min-height: 20px;
	}

	.dash-camera-widget-header.dense-overlay.size-s .live-dot :global(.live-indicator-wrapper) {
		transform: scale(0.85);
		transform-origin: center;
	}

	.header-status-column {
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: var(--spacing-xs);
		min-width: 0;
		max-width: 100%;
	}
</style>
